import { CuriosityAutocomplete, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Operator } from '@procureai/query-builder';
import {
  ValueEditorDate,
  ValueEditorDateRange,
  ValueEditorNumberInput,
  ValueEditorNumberRange,
} from '@procureai/query-builder-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgThreadFilterField, SvbrgIntakeStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  UseDataGridQueryBuilderConfig,
  listOperators,
  operators,
  queryFieldFactory,
} from '~/components/DataGridQueryBuilder';
import { ValueEditorSelectAutocomplete } from '~/components/ValueEditorSelectAutocomplete/ValueEditorSelectAutocomplete';

enum IntakeListQueryFieldGroup {
  REQUEST = 'request',
}

export const useIntakeListDataGridQueryBuilderConfig: UseDataGridQueryBuilderConfig = () => {
  const { t } = useTranslation(I18nNamespace.intake);
  return useMemo(() => {
    const createField = queryFieldFactory(t, { i18nPrefix: 'IntakeListQueryBuilder' });
    return {
      fields: [
        createField({
          name: SvbrgThreadFilterField.sequenceNumber,
          grp: IntakeListQueryFieldGroup.REQUEST,
          operators: operators.numNotEmpty,
        }),
        createField({
          name: SvbrgThreadFilterField.status,
          grp: IntakeListQueryFieldGroup.REQUEST,
          operators: operators.enumValue,
        }),
        createField({
          name: SvbrgThreadFilterField.createdAt,
          grp: IntakeListQueryFieldGroup.REQUEST,
          operators: operators.date,
        }),
        createField({
          name: SvbrgThreadFilterField.createdBy,
          grp: IntakeListQueryFieldGroup.REQUEST,
          operators: operators.id,
        }),
      ],
      getOperatorLabel: ({ field, operator }) => {
        switch (field) {
          case SvbrgThreadFilterField.createdAt:
            if (operator === Operator.LT || operator === Operator.GT) {
              return `QueryBuilder.operatorDate.${operator}`;
            }
        }
      },
      renderValueEditor: ({ field, operator, ...otherProps }) => {
        if (operator === Operator.EMPTY || operator === Operator.NOT_EMPTY) {
          return null;
        }
        switch (field) {
          //
          case SvbrgThreadFilterField.status:
            return (
              <ValueEditorSelectAutocomplete
                {...otherProps}
                initialI18nPrefix="IntakeStatus"
                initialOptions={Object.keys(SvbrgIntakeStatus)}
                multiple={listOperators[operator]}
              />
            );
          case SvbrgThreadFilterField.createdBy:
            return <CuriosityAutocomplete {...otherProps} simplifyValue ns={CuriosityNamespaceType.INTERNAL_USER} />;
          case SvbrgThreadFilterField.sequenceNumber:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorNumberRange {...otherProps} />;
            }
            return <ValueEditorNumberInput min={0} {...otherProps} />;
          case SvbrgThreadFilterField.createdAt:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorDateRange {...otherProps} />;
            }
            return <ValueEditorDate {...otherProps} />;
          default:
            return null;
        }
      },
    };
  }, [t]);
};
